import React, { useContext, useState } from "react";
import LanguageContext from "../../utils/Contexts/LanguageContext";
import moment from "moment";
import { Loader } from "../Common";

import "./style.css";
import { useNavigate } from "react-router-dom";
import Constant from "../Constants";

const ArticalTable = ({
  data,
  columnsVisibility,
  setColumnSorts,
  columnSorts,
}) => {
  const { t } = useContext(LanguageContext);
  const navigate = useNavigate();

  const { niches, languageOptions, contractStatusType, categoryOptions } =
    Constant();
  const [expandedRows, setExpandedRows] = useState({});

  const toggleExpand = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const setColumnSort = (columnIndex) => {
    setColumnSorts((prevSorts) => ({
      ...prevSorts,
      [columnIndex]:
        prevSorts[columnIndex] === "asc"
          ? "desc"
          : prevSorts[columnIndex] === "desc"
          ? null
          : "asc",
    }));
  };

  const columnsHeader = [
    { key: "title", label: t("Title"), isSortable: true, sortId: 0 },
    { key: "contract", label: t("Contract"), isSortable: false },
    { key: "status", label: t("Status"), isSortable: true, sortId: 1 },
    { key: "created", label: t("Created"), isSortable: true, sortId: 2 },
  ];

  const formatWordCount = (wordCount) => {
    if (!wordCount || wordCount === 0) {
      return `0 ${t("Words")}`;
    }
    if (wordCount === 1) {
      return `1 ${t("Word")}`;
    }
    return `${wordCount} ${t("Words")}`;
  };

  const renderNicheContent = (niche, nicheType) => {
    if (!niche?.passed && !nicheType) return <Loader />;
    const matchedNiche = niches.find((n) => n.value === nicheType);
    return matchedNiche ? (
      <span className="article-table-tags bg-soft-secondary rounded-circle icon-gray">
        {matchedNiche.content}
      </span>
    ) : null;
  };

  const renderMediaContent = (item) => {
    const { images, videos } = item?.quality || {};
    return (
      <div
        className={`d-flex align-items-center ${
          (images === null || videos === null) && "gap-3"
        }`}
      >
        {images !== null ? (
          <span
            className="d-flex align-items-center bg-soft-secondary text-secondary hide-circle"
            style={{
              borderRadius: "100px",
              width: "35px",
              height: "23px",
              fontSize: "12px",
              justifyContent: "left",
              paddingLeft: "4px",
              gap: "4px",
              border: "1.5px solid var(--bs-card-bg)",
            }}
          >
            {images} <i className="bi bi-card-image"></i>
          </span>
        ) : (
          <Loader />
        )}
        {videos !== null ? (
          <span
            className="d-flex align-items-center bg-soft-secondary text-secondary position-relative z-index-10"
            style={{
              borderRadius: "100px",
              marginLeft: "-6px",
              width: "35px",
              height: "23px",
              fontSize: "12px",
              justifyContent: "right",
              border: "1.5px solid var(--bs-card-bg)",
            }}
          >
            {videos} <i className="bi bi-play fs-3"></i>
          </span>
        ) : (
          <Loader />
        )}
      </div>
    );
  };

  const readability = (readability) => {
    return (
      !readability?.passed && (
        <div className="d-flex justify-content-center gap-2 align-items-center">
          <span
            className="cross-icon"
            style={{ height: "16px", width: "16px", padding: "2px" }}
          >
            <i className="bi bi-x" style={{ fontSize: "16px" }}></i>
          </span>
          <span
            className="custom-text-gray"
            style={{ fontSize: "12px", fontWeight: "600" }}
          >
            {t("Readability")}
          </span>
        </div>
      )
    );
  };

  const renderContent = (content, label) => {
    if (!content)
      return (
        <span>
          <Loader />
        </span>
      );
    if (content?.passed) return null;

    return (
      <div className="d-flex justify-content-center gap-2 align-items-center">
        <span
          className="cross-icon"
          style={{ height: "16px", width: "16px", padding: "2px" }}
        >
          <i className="bi bi-x" style={{ fontSize: "16px" }}></i>
        </span>
        <span
          className="custom-text-gray"
          style={{ fontSize: "12px", fontWeight: "600" }}
        >
          {label}
        </span>
      </div>
    );
  };

  const navigateToOpenDetails = (e, uuid) => {
    e.stopPropagation();
    navigate(`/contracts/${uuid}`);
  };

  const handleEditArticles = (e, id, item) => {
    const draftArticle = {
      drfat: true,
      data: item,
    };
    e.stopPropagation();
    navigate(`/content/editor/${id}`, { state: { draftArticle } });
  };

  const columnHeaderValue = {
    title: {
      value: (item) => {
        const isExpanded = expandedRows[item?.uuid] || false;
        return (
          <>
            <h1 className="fw-bold m-0" style={{ fontSize: "16px" }}>
              {item?.title}
            </h1>
            <div className="d-flex align-items-center gap-3 mt-2">
              {languageOptions[item?.quality?.language_type] ? (
                <span className="article-table-tags bg-soft-secondary text-secondary">
                  {languageOptions[item?.quality?.language_type]}
                </span>
              ) : (
                <Loader />
              )}
              {categoryOptions[item?.quality?.category_type] ? (
                <span className="article-table-tags bg-soft-secondary text-secondary">
                  {categoryOptions[item?.quality?.category_type]}
                </span>
              ) : (
                <Loader />
              )}
              <span className="d-flex justify-content-center align-items-center">
                {renderNicheContent(
                  item?.quality?.niche,
                  item?.quality?.niche_type
                )}
              </span>
              {renderMediaContent(item)}
              <span className="article-table-tags bg-soft-secondary text-secondary">
                {formatWordCount(item?.quality?.words)}
              </span>
              {renderContent(item?.quality?.ai, t("AI Detecting"))}
              {readability(item?.quality?.readability)}
              {renderContent(item?.quality?.plagiarism, t("Plagiarism"))}
              {renderContent(item?.quality?.seo, t("SEO"))}
            </div>
            <div className="d-flex align-content-center align-items-center gap-3 mt-2 flex-wrap">
              {(isExpanded ? item?.links : item?.links?.slice(0, 3))?.map(
                (link) => (
                  <div className="d-flex align-items-center gap-2">
                    {" "}
                    <i class="bi bi-link-45deg"></i>{" "}
                    <a
                      href={link?.to}
                      className={`link-font-style ${
                        link?.rel === 1 && "text-primary"
                      }`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {link?.anchor}
                    </a>
                  </div>
                )
              )}
              {item?.links.length > 3 && !isExpanded && (
                <span
                  className="link-font-style text-muted"
                  style={{ cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(item?.uuid);
                  }}
                >
                  + {item?.links.length - 3} more
                </span>
              )}
            </div>
          </>
        );
      },
    },
    contract: {
      value: (item) => (
        <span
          className="text-center w-100 d-block cursor-pointer"
          onClick={(e) => navigateToOpenDetails(e, item?.contract?.uuid)}
        >
          {item?.contract?.code ?? <span className="text-secondary">─</span>}
        </span>
      ),
      className: (item) =>
        item?.contract?.code
          ? `${
              contractStatusType[item?.status]?.contractClass
            } d-flex justify-content-center align-items-center`
          : "",
    },
    created: {
      value: (item) => {
        const createdDate = moment(item?.created);
        const today = moment();
        const daysDiff = today.diff(createdDate, "days");
        return daysDiff === 0
          ? t("Today")
          : daysDiff === 1
          ? `1${t("d ago")}`
          : `${daysDiff}${t("d ago")}`;
      },
      className: (item) => "custom-text-gray",
    },
    status: {
      value: (item) => contractStatusType[item?.status]?.title,
      className: (item) =>
        `${
          contractStatusType[item?.status]?.class
        } pt-1 pb-2 border-radius-5px d-block text-center`,
    },
  };

  return (
    <div className="table-responsive datatable-custom">
      <table
        id="datatable"
        className="table table-lg table-borderless table-thead-bordered table-nowrap table-align-middle card-table"
      >
        <thead className="thead-light">
          <tr>
            {columnsHeader.map(({ key, label, isSortable, sortId }) =>
              columnsVisibility[key] ? (
                <th
                  key={key}
                  className={`${isSortable ? "sorting" : ""}${
                    isSortable && !columnSorts[sortId]
                      ? ""
                      : columnSorts[sortId] === "asc"
                      ? "_asc"
                      : "_desc"
                  }`}
                  style={{
                    width: key === "title" ? "auto" : "150px",
                    fontSize: "14px",
                    fontWeight: "600",
                  }}
                  onClick={() => (isSortable ? setColumnSort(sortId) : {})}
                >
                  {t(label)}
                </th>
              ) : null
            )}
          </tr>
        </thead>
        <tbody id="nav-one-eg1" aria-labelledby="nav-one-eg1-tab">
          {!!data?.length ? (
            data.map((item, index) => (
              <tr
                key={index}
                className={index === data.length - 1 ? "" : "border-bottom"}
                onClick={
                  item?.status === 0
                    ? (e) => handleEditArticles(e, item?.uuid, item)
                    : undefined
                }
                style={{ cursor: item?.status === 0 ? "pointer" : "default" }}
              >
                {Object.entries(columnsVisibility)?.map(([key, isVisible]) => {
                  return (
                    isVisible && (
                      <td key={key}>
                        <span
                          className={`${columnHeaderValue[key]?.className?.(
                            item
                          )}`}
                          style={{
                            width: key !== "title" ? "157px" : "auto",
                            height: key !== "title" ? "29px" : "auto",
                            verticalAlign: "middle !important",
                          }}
                        >
                          {columnHeaderValue[key]?.value(item)}
                        </span>
                      </td>
                    )
                  );
                })}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="100%">
                <div className="text-center p-4 no-data-wrapper">
                  <img
                    className="mb-3"
                    src="https://static.articlesfactory.ai/app/svg/illustrations-light/oc-error.svg"
                    alt=""
                    style={{ width: "10rem" }}
                    data-hs-theme-appearance="dark"
                  />
                  <img
                    className="mb-3"
                    src="https://static.articlesfactory.ai/app/svg/illustrations/oc-error.svg"
                    alt=""
                    style={{ width: "10rem" }}
                    data-hs-theme-appearance="default"
                  />
                  <p className="mb-0">
                    {!data ? t("Loading data") : t("No data to show")}
                  </p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ArticalTable;
